/**
 * React wiring for the session's one event stream: the provider opens it when the session starts
 * and closes it when the session ends, invalidates the default queries for each event
 * (live-invalidations.ts), and gives pages two hooks:
 *
 *   useLiveEvent(type, handler)           — react to an event (a toast, a progress line)
 *   useLiveInvalidation(types, queryKeys) — refetch these queries when any of these events arrive
 *
 * and useLiveState() for the top bar's live indicator.
 */
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { EventStream, type LiveEvent, type LiveEventHandler, type StreamState } from './event-stream';
import { LIVE_INVALIDATIONS } from './live-invalidations';

const StreamContext = createContext<EventStream | null>(null);

/** Subscribe the default invalidations. Returns the unsubscribe. Exported for the test. */
export function wireDefaultInvalidations(
  stream: Pick<EventStream, 'subscribe'>,
  queryClient: Pick<QueryClient, 'invalidateQueries'>,
  map: Readonly<Record<string, readonly QueryKey[]>> = LIVE_INVALIDATIONS,
): () => void {
  const offs = Object.entries(map).map(([type, keys]) => stream.subscribe(type, () => {
    for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey });
  }));
  return () => { for (const off of offs) off(); };
}

export interface LiveEventsProviderProps {
  /** Made once per session by the caller (so a test can pass a stream over a fake source). */
  readonly stream: EventStream;
  readonly children: ReactNode;
}

export function LiveEventsProvider({ stream, children }: LiveEventsProviderProps) {
  const queryClient = useQueryClient();
  useEffect(() => {
    const off = wireDefaultInvalidations(stream, queryClient);
    void stream.connect();
    return () => { off(); stream.disconnect(); };
  }, [stream, queryClient]);
  return <StreamContext.Provider value={stream}>{children}</StreamContext.Provider>;
}

function useStream(): EventStream {
  const stream = useContext(StreamContext);
  if (!stream) throw new Error('useLiveEvent / useLiveInvalidation need <LiveEventsProvider>');
  return stream;
}

export function useLiveEvent(type: string, handler: LiveEventHandler): void {
  const stream = useStream();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => stream.subscribe(type, (e: LiveEvent) => ref.current(e)), [stream, type]);
}

export function useLiveInvalidation(types: readonly string[], queryKeys: readonly QueryKey[]): void {
  const stream = useStream();
  const queryClient = useQueryClient();
  const typesKey = types.join('|');
  const keysKey = JSON.stringify(queryKeys);
  useEffect(() => {
    const keys = JSON.parse(keysKey) as QueryKey[];
    const offs = typesKey.split('|').filter(Boolean).map((t) => stream.subscribe(t, () => {
      for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey });
    }));
    return () => { for (const off of offs) off(); };
  }, [stream, queryClient, typesKey, keysKey]);
}

export function useLiveState(): StreamState {
  const stream = useContext(StreamContext);
  const [state, setState] = useState<StreamState>(stream?.state ?? 'offline');
  useEffect(() => {
    if (!stream) return;
    setState(stream.state);
    return stream.onState(setState);
  }, [stream]);
  return state;
}
